/**
 * A guard that takes effect on the tap rather than on the next render.
 *
 * Every submit handler in the auth flow disables itself the same way:
 *
 *     setLoading(true);          // and the button reads `disabled={loading}`
 *
 * `setLoading` is a state update. The button does not actually become disabled
 * until React re-renders, and between the tap and that render the JS thread is
 * free to deliver another press — a fast double tap, or the keyboard's submit
 * key racing the button when a handler is wired to both. The second press runs
 * the whole handler again, and the first request is still in flight.
 *
 * For sign-in that is not cosmetic. `/auth/start` mints ONE live code per
 * address, so a duplicate start invalidates the code the first one has already
 * emailed, and the user is told a correct code is wrong. The server now
 * coalesces duplicates itself (`auth.service.ts`), which is what protects
 * builds already installed; this stops them being sent at all, and stops the
 * duplicate navigation the server cannot see.
 *
 * Deliberately a plain object rather than a hook: it holds no React state — the
 * whole point is that it changes value synchronously, which state cannot — and
 * as a leaf module with no imports it can be tested directly under `node
 * --test` instead of asserted against as source text.
 */
export type Latch = {
  /** Takes the latch. `false` means someone else holds it: do nothing. */
  claim(): boolean;
  /** Gives it back. Safe to call when not held. */
  release(): void;
  readonly held: boolean;
};

export function createLatch(): Latch {
  let held = false;
  return {
    claim() {
      if (held) return false;
      held = true;
      return true;
    },
    release() {
      held = false;
    },
    get held() {
      return held;
    },
  };
}
